import {
    collectAssemblyNodeIdentityLookupIds,
    normalizeAssemblyNodeId,
    splitAssemblyNodePath,
} from '../../assembly/utils/AssemblyNodePath';

/**
 * Worker 几何运行时对象所有权条目。
 *
 * 设计说明：该条目描述一个几何对象在运行时中的归属范围，后续 scoped-subtree commit
 * 可以据此判断目标子树是否独占某个几何对象。
 */
export interface GeometryRuntimeOwnershipEntry {
    /** 几何对象所属的运行时稳定键。 */
    readonly runtimeKey: string;
    /** 拥有该几何对象的装配节点 ID。 */
    readonly assemblyNodeId: string;
    /** 拥有该几何对象的组件实例 ID，零件文档或无组件实例时为空。 */
    readonly componentInstanceId?: string;
    /** 与导入 body 对应的绑定 ID，非导入 body 时为空。 */
    readonly bodyBindingId?: string;
    /** Worker 几何状态中的几何对象 ID。 */
    readonly geometryObjectId: string;
    /** BRep 内容哈希，用于识别同源几何内容，尚无哈希时为空。 */
    readonly brepSha256?: string;
    /** 该 ownership 覆盖的稳定节点键路径集合。 */
    readonly scopeNodeKeys: readonly string[];
}

/**
 * 几何对象释放判定输入。
 */
export interface GeometryRuntimeOwnershipReleaseInput {
    /** 需要判断是否可以释放的 Worker 几何对象 ID。 */
    readonly geometryObjectId: string;
    /** 本次 scoped-subtree 操作的目标节点稳定键。 */
    readonly targetNodeKey: string;
}

/**
 * Worker scoped-subtree 几何运行时所有权索引。
 *
 * 设计说明：
 * 1. 构造函数只接收 ownership 快照，并立即复制，避免外部数组或条目后续变更污染索引；
 * 2. 查询结果再次复制，避免调用方修改返回值后反向污染内部索引；
 * 3. 释放判定以 geometryObjectId 的所有 owner 为准，只有目标子树独占时才允许释放。
 */
export class GeometryRuntimeOwnershipIndex {
    private readonly entries: GeometryRuntimeOwnershipEntry[];
    private readonly entriesByGeometryObjectId: Map<string, GeometryRuntimeOwnershipEntry[]>;

    /**
     * 创建 scoped-subtree ownership 索引。
     *
     * @param entries 当前 runtime ownership 快照条目。
     */
    constructor(entries: readonly GeometryRuntimeOwnershipEntry[]) {
        const copiedEntries = entries.map((entry) => GeometryRuntimeOwnershipIndex.cloneEntry(entry));

        this.entries = copiedEntries;
        this.entriesByGeometryObjectId = GeometryRuntimeOwnershipIndex.createGeometryObjectIdIndex(copiedEntries);
    }

    /**
     * 查询目标子树覆盖的 ownership 条目。
     *
     * @param targetNodeKey scoped-subtree 的目标节点稳定键。
     * @returns 所有 scopeNodeKeys 包含目标节点键的 ownership 条目副本。
     */
    getEntriesBySubtree(targetNodeKey: string): GeometryRuntimeOwnershipEntry[] {
        const normalizedTargetNodeKey = GeometryRuntimeOwnershipIndex.normalizeNodeKey(targetNodeKey);
        const entries = this.entries.filter((entry) => (
            entry.scopeNodeKeys.some((scopeNodeKey) => (
                GeometryRuntimeOwnershipIndex.isScopeNodeKeyInsideTargetSubtree(
                    scopeNodeKey,
                    normalizedTargetNodeKey,
                )
            ))
        ));

        // 返回深拷贝，确保调用方无法通过修改结果破坏索引快照。
        return entries.map((entry) => GeometryRuntimeOwnershipIndex.cloneEntry(entry));
    }

    /**
     * 判断目标子树是否可以释放指定几何对象。
     *
     * @param input 几何对象释放判定输入。
     * @returns 几何对象存在且全部 owner 都属于目标子树时返回 true。
     */
    canReleaseGeometryObject(input: GeometryRuntimeOwnershipReleaseInput): boolean {
        const owners = this.entriesByGeometryObjectId.get(input.geometryObjectId);

        if (!owners || owners.length === 0) {
            return false;
        }

        const normalizedTargetNodeKey = GeometryRuntimeOwnershipIndex.normalizeNodeKey(input.targetNodeKey);

        // 同一个 geometryObjectId 可能被多个组件共享，只要存在非目标子树 owner 就不能释放。
        return owners.every((entry) => (
            entry.scopeNodeKeys.some((scopeNodeKey) => (
                GeometryRuntimeOwnershipIndex.isScopeNodeKeyInsideTargetSubtree(
                    scopeNodeKey,
                    normalizedTargetNodeKey,
                )
            ))
        ));
    }

    /**
     * 创建按 geometryObjectId 查询 owner 的索引。
     *
     * @param entries 已经完成防御性复制的 ownership 条目。
     * @returns geometryObjectId 到 ownership owner 条目的映射。
     */
    private static createGeometryObjectIdIndex(
        entries: readonly GeometryRuntimeOwnershipEntry[],
    ): Map<string, GeometryRuntimeOwnershipEntry[]> {
        const index = new Map<string, GeometryRuntimeOwnershipEntry[]>();

        entries.forEach((entry) => {
            const owners = index.get(entry.geometryObjectId) ?? [];
            owners.push(entry);
            index.set(entry.geometryObjectId, owners);
        });

        return index;
    }

    /**
     * 复制 ownership 条目。
     *
     * @param entry 待复制的 ownership 条目。
     * @returns 与输入值等价但不共享数组引用的新条目。
     */
    private static cloneEntry(entry: GeometryRuntimeOwnershipEntry): GeometryRuntimeOwnershipEntry {
        return {
            runtimeKey: entry.runtimeKey,
            assemblyNodeId: entry.assemblyNodeId,
            componentInstanceId: entry.componentInstanceId,
            bodyBindingId: entry.bodyBindingId,
            geometryObjectId: entry.geometryObjectId,
            brepSha256: entry.brepSha256,
            scopeNodeKeys: [...entry.scopeNodeKeys],
        };
    }

    /**
     * 归一化项目模型节点 key。
     *
     * @param nodeKey 原始节点 key。
     * @returns 使用统一分隔符且去掉首尾分隔符的节点 key。
     */
    private static normalizeNodeKey(nodeKey: string): string {
        return normalizeAssemblyNodeId(nodeKey);
    }

    /**
     * 判断两个节点 key 是否指向同一个装配节点身份。
     *
     * 设计说明：离线包、项目模型与历史快照会混用 URL 编码和未编码的节点 ID。
     * Worker scoped release 只需要精确身份等价，不使用尾部路径模糊匹配，避免同名兄弟节点被误判为同一 owner。
     *
     * @param left 左侧节点 key。
     * @param right 右侧节点 key。
     * @returns 两侧 lookup ID 存在交集时返回 true。
     */
    private static areNodeKeysEquivalent(left: string, right: string): boolean {
        const leftLookupIds = collectAssemblyNodeIdentityLookupIds(left);
        if (leftLookupIds.length === 0) {
            return false;
        }

        const rightLookupIdSet = new Set(collectAssemblyNodeIdentityLookupIds(right));
        return leftLookupIds.some((lookupId) => rightLookupIdSet.has(lookupId));
    }

    /**
     * 收集节点 key 自根到自身的装配路径。
     *
     * @param nodeKey 节点 key。
     * @returns 已规范化的祖先路径集合，顺序为根到自身。
     */
    private static collectNodeKeyAncestorPath(nodeKey: string): string[] {
        const segments = splitAssemblyNodePath(nodeKey);
        return segments.map((_, index) => segments.slice(0, index + 1).join('/'));
    }

    /**
     * 判断 owner 节点是否落在目标子树内。
     *
     * 设计说明：一级节点切换会替换该节点的全部后代数据，包括轻量 ZIP/GLB sidecar
     * 注册出来的后代 BRep；因此 scoped ownership 必须使用子树前缀语义，而不是只匹配
     * 目标节点本身。
     *
     * @param scopeNodeKey owner 所属节点 key。
     * @param normalizedTargetNodeKey 已归一化的目标子树根节点 key。
     * @returns owner 是否属于目标子树。
     */
    private static isScopeNodeKeyInsideTargetSubtree(
        scopeNodeKey: string,
        normalizedTargetNodeKey: string,
    ): boolean {
        const normalizedScopeNodeKey = GeometryRuntimeOwnershipIndex.normalizeNodeKey(scopeNodeKey);
        if (!normalizedTargetNodeKey || !normalizedScopeNodeKey) {
            return normalizedScopeNodeKey === normalizedTargetNodeKey;
        }

        return normalizedScopeNodeKey === normalizedTargetNodeKey
            || normalizedScopeNodeKey.startsWith(`${normalizedTargetNodeKey}/`)
            || GeometryRuntimeOwnershipIndex.areNodeKeysEquivalent(
                normalizedScopeNodeKey,
                normalizedTargetNodeKey,
            )
            || GeometryRuntimeOwnershipIndex.collectNodeKeyAncestorPath(normalizedScopeNodeKey)
                .some((ancestorNodeKey) => GeometryRuntimeOwnershipIndex.areNodeKeysEquivalent(
                    ancestorNodeKey,
                    normalizedTargetNodeKey,
                ));
    }
}
